import React, { useEffect, useState } from 'react';
import InputForm from './InputForm';
import SummarySection from './SummarySection';
import SavedCalculations from './SavedCalculations';
import TermSheetHistory from './TermSheetHistory';
import PaymentScheduleSidebar from './PaymentScheduleSidebar';
import { CalculationInputs, RentalScheduleItem, CalculationSummary, generateRentalSchedule, calculateSummary } from '../../utils/calculationUtils';
import { goalSeek } from '../../utils/goalSeekUtils';
import { Button } from "../ui/button";
import { Calculator, Save, Eye, Menu, BarChart3, History, FileText } from "lucide-react";
import IRRCheckDialog from './IRRCheckDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/collapsible";
import { toast } from "react-toastify";
import apiService from '../../services/apiService';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

interface SavedCalculation {
  id: string;
  name: string;
  timestamp: Date;
  inputs: CalculationInputs;
  schedule: RentalScheduleItem[];
  summary: CalculationSummary;
  calculatedValues?: any;
}

interface RentalCalculatorProps{
  customerId?:string;
}

interface DownloadedTermSheet {
  id: string;
  calculationId: string;
  calculationName: string;
  downloadedAt: Date;
  downloadedBy: string;
  inputs: CalculationInputs;
  summary: CalculationSummary;
  status: 'review' | 'rejected' | 'approved';
  approvedAt?: Date;
  approvedBy?: string;
  remark?: string;
}

interface ApiResponse<T> {
  data: T;
  downloaded:T;
  message?: string;
  status?: number;
  // financial?:FinancialTabProps
}

const RentalCalculatorLayout: React.FC<RentalCalculatorProps> = ({customerId}) => {
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isScheduleSidebarOpen, setIsScheduleSidebarOpen] = useState(false);
  const [isIRRDialogOpen, setIsIRRDialogOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState<SavedCalculation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadedTermSheets, setDownloadedTermSheets] = useState<DownloadedTermSheet[]>([]);
  const [activeTab, setActiveTab] = useState('calculator');
  const [isSaving, setIsSaving] = useState(false);

  const [inputs, setInputs] = useState<CalculationInputs>({
    totalMonths: 24,
    baseRental: 847.5,
    totalRental: 1000,
    gstRate: 0.18,
    ptpmConsideration: 847.5,
    discountingFactor: 0.095,
    gstPassbackEnabled: false,
    disbursementAmount: 1000,
    rentalCalculationBasis: 'preGstDiscounting',
    securityDeposit: 100,
    securityDepositPercentage: 10,
    processingFee: 0,
    processingFeePercentage: 1.5,
    refundableSecurity: 0,
    refundableSecurityPercentage: 5,
    rentalFrequency: 'monthly',
    dueDate: new Date().toISOString().split('T')[0],
    disbursementDate: new Date().toISOString().split('T')[0],
    advanceRentalEnabled: false,
    advanceRentalCount: 0,
    advanceRentalType: 'periods',
  });
  const [schedule, setSchedule] = useState<RentalScheduleItem[]>([]);
  const [summary, setSummary] = useState<CalculationSummary>({
    disbursementAmount: 0,
    totalRentalWithGst: 0,
    totalRentalPostTds: 0,
    totalCost: 0,
    gstAmount: 0,
    baseRental: 0,
    securityDeposit: 0,
    processingFee: 0,
    totalCapitalAvailable: 0,
    cashInHand: 0,
    cashFlowToAstrafin: 0,
    tdsOnCashFlow: 0,
    effectiveCashFlow: 0,
    refundableSecurity: 0,
    gstPayableAfterSetoff: 0,
    cashAvailable: 0,
    expensesLeft: 0,
    marginAvailable: 0,
    marginPercentage: 0,
    isNegativeMargin: false,
    advanceRentals: 0,
    gstOnAdvanceRentals: 0,
    totalAdvanceRentalInclGst: 0,
    advanceRentalEnabled: false,
  });

  const fetchTermSheetData = async () =>{
    if (!customerId) {
      // setError("Customer ID is required");
      // setIsLoading(false);
      return;
    }

    try {
      // toast.warning("Please complete a calculation before saving.",
      // );
      setIsLoading(true);
      // const saved = localStorage.getItem('savedCalculations');
      // setLoading(true);
      const [termsheetResponse] = await Promise.all([
        apiService.get<ApiResponse<any>>(`/customers/get-term-sheet/${customerId}`)
      ]);

      const saved = termsheetResponse.data;

      if (saved) {
        const parsedCalculations = saved.map((calc: any) => ({
          ...calc,
          timestamp: new Date(calc.timestamp)
        }));
        setSavedCalculations(parsedCalculations);
      }
  
      const savedTermSheets = termsheetResponse.downloaded;
      if (savedTermSheets) {
        const parsedTermSheets = savedTermSheets.map((sheet: any) => ({
          ...sheet,
          downloadedAt: new Date(sheet.downloadedAt),
          approvedAt: sheet.approvedAt ? new Date(sheet.approvedAt) : undefined,
          status: sheet.status || 'review' // Ensure status has a default value
        }));
        setDownloadedTermSheets(parsedTermSheets);
      }

     
    } catch (err) {
      console.error("Error fetching data:", err);
      // setError("Failed to fetch customer data");
      // toast.error("Failed to load customer profile");
    } finally {
      // setLoading(false);
      setIsLoading(false);
    }

  }

  useEffect(() => {
    if (!customerId) return;
    if (activeTab === "termsheets" || activeTab === "saved") {
      fetchTermSheetData();
    }
  }, [activeTab, customerId]);

  const handleUpdateCalculation = (updated: SavedCalculation) => {
    setSavedCalculations(prev =>
      prev.map(c => c.id === updated.id ? updated : c)
    );
  };

  const handleTermSheetDeleted = (calculationId: string) => {
    setSavedCalculations((prev) => prev.filter((c) => c.id !== calculationId));
  };

  // Load saved calculations and term sheets from localStorage on component mount
  useEffect( () => {
    fetchTermSheetData();    
  }, []);

  useEffect(() => {
    if (!customerId && (activeTab === "saved" || activeTab === "termsheets")) {
      setActiveTab("calculator");
    }
  }, [customerId, activeTab]);

  const handleInputChange = (key: keyof CalculationInputs, value: any) => {
    setInputs((prev) => {
      const newInputs = { ...prev, [key]: value };
      if (key === 'totalRental') {
        newInputs.disbursementAmount = value;
      }
      return newInputs;
    });
  };

  const handleCalculate = async () => {
    setIsCalculating(true);
    
    try {
      setTimeout(() => {
        const calculatedDisbursementAmount = goalSeek(inputs);
        
        const processingFee = (inputs.processingFeePercentage / 100) * inputs.totalRental;
        const refundableSecurity = (inputs.refundableSecurityPercentage / 100) * inputs.totalRental;
        
        const updatedInputs = { 
          ...inputs, 
          disbursementAmount: calculatedDisbursementAmount,
          processingFee,
          refundableSecurity
        };
        setInputs(updatedInputs);
        
        const calculatedSchedule = generateRentalSchedule(updatedInputs);
        setSchedule(calculatedSchedule);
        
        const calculatedSummary = calculateSummary(updatedInputs, calculatedSchedule);
        setSummary(calculatedSummary);
        
        toast.success(`Calculation Complete! Disbursement: ${new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
          }).format(calculatedDisbursementAmount)}`);
        
        setHasCalculated(true);
        setIsCalculating(false);
        setIsSummaryOpen(true);
      }, 100);
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error("An error occurred during calculation. Please check your inputs.");
      setIsCalculating(false);
    }
  };

  const handleSaveCalculation = async () => {
    if (!customerId) {
      toast.error("Customer ID is required to save.");
      return;
    }
    if (!hasCalculated) {
      toast("Please complete a calculation before saving.");
      return;
    }
    if (isSaving) return;

    setIsSaving(true);
    const calculationName = `Calculation ${savedCalculations.length + 1}`;

    
    const calculateEnhancedValues = () => {
      const baseRental = inputs.totalRental / (1 + inputs.gstRate);
      const gstAmount = baseRental * inputs.gstRate;
  
      // GST Passback calculation - adjusted for quarterly frequency
      let gstPassback = 0;
      if (inputs.gstPassbackEnabled) {
        if (inputs.rentalFrequency === 'quarterly') {
          gstPassback = gstAmount / (inputs.totalMonths / 3);
        } else {
          gstPassback = gstAmount / inputs.totalMonths;
        }
        const round2 = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;

        gstPassback = round2(gstPassback);
        inputs.gstPassback = gstPassback;

      }
  
      // PTPM with GST Passback consideration
      let ptpmWithPassback = inputs.ptpmConsideration;
      if (inputs.gstPassbackEnabled) {
        ptpmWithPassback = inputs.ptpmConsideration - gstPassback;
      }
  
      // Final rental calculation based on Rental Calculation Basis
      let finalRental = 0;
      let finalRentalDescription = "";
  
      switch (inputs.rentalCalculationBasis) {
        case 'preGstDiscounting':
          finalRental = ptpmWithPassback;
          finalRentalDescription = "Pre GST Discounting (PTPM as Final)";
          break;
        case 'postGstDiscounting':
          finalRental = ptpmWithPassback * (1 + inputs.gstRate);
          finalRentalDescription = "Post GST Discounting (PTPM + GST)";
          break;
        case 'postTdsDiscounting':
          // PTPM*98% + GST on PTPM
          const ptpmAfterTds = ptpmWithPassback * 0.98;
          const gstOnPtpm = ptpmWithPassback * inputs.gstRate;
          finalRental = ptpmAfterTds + gstOnPtpm;
          finalRentalDescription = "Post TDS Discounting (PTPM*98% + GST on PTPM)";
          break;
        default:
          finalRental = ptpmWithPassback;
          finalRentalDescription = "Standard Calculation";
      }
  
      return {
        baseRental,
        gstAmount,
        gstPassback,
        ptpmWithPassback,
        finalRental,
        finalRentalDescription,
        tdsAmount: ptpmWithPassback * 0.02,
        securityDeposit: (inputs.securityDepositPercentage / 100) * inputs.totalRental,
        processingFee: (inputs.processingFeePercentage / 100) * inputs.totalRental,
        refundableSecurity: (inputs.refundableSecurityPercentage / 100) * inputs.totalRental
      };
    };

    const enhancedValues = calculateEnhancedValues();


    const newCalculation: SavedCalculation = {
      id: Date.now().toString(),
      name: calculationName,
      timestamp: new Date(),
      inputs: { ...inputs },
      schedule: [...schedule],
      summary: { ...summary },
      calculatedValues: enhancedValues
    };

    try {
      setIsLoading(true);
      await apiService.post<ApiResponse<any>>(
        `/customers/update-term-sheet`,
        {
          customerId,
          calculation: newCalculation,
        }
      );
      fetchTermSheetData();
      const updatedCalculations = [...savedCalculations, newCalculation];
      setSavedCalculations(updatedCalculations);
      toast.success("Calculation has been saved successfully.");
    } catch (err) {
      toast.error("Failed to save calculation.");
    } finally {
      setIsLoading(false);
      setIsSaving(false);
    }
  };


  const handleLoadCalculation = (calculation: SavedCalculation) => {
    setInputs(calculation.inputs);
    setSchedule(calculation.schedule);
    setSummary(calculation.summary);
    setHasCalculated(true);
    setActiveTab('calculator');
    toast("Calculation Loaded");
  };

  const handleTermSheetDownload = (calculation: SavedCalculation) => {
    const newTermSheet: DownloadedTermSheet = {
      id: Date.now().toString(),
      calculationId: calculation.id,
      calculationName: calculation.name,
      downloadedAt: new Date(),
      downloadedBy: 'Admin User',
      inputs: { ...calculation.inputs },
      summary: { ...calculation.summary },
      status: 'review'
    };

    const updatedTermSheets = [...downloadedTermSheets, newTermSheet];
    setDownloadedTermSheets(updatedTermSheets);
    // localStorage.setItem('downloadedTermSheets', JSON.stringify(updatedTermSheets));

    toast("Term Sheet Generated",
     );
  };

  const handleUpdateTermSheet = (updatedTermSheet: DownloadedTermSheet) => {
    const updatedTermSheets = downloadedTermSheets.map(sheet => 
      sheet.id === updatedTermSheet.id ? updatedTermSheet : sheet
    );
    setDownloadedTermSheets(updatedTermSheets);
    // localStorage.setItem('downloadedTermSheets', JSON.stringify(updatedTermSheets));
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 to-blue-50 w-full">
       {isLoading && (
  <div className="fixed inset-0 bg-white/50 backdrop-blur-sm z-50 flex items-center justify-center">
    <div className="bg-white p-5 rounded-lg shadow-lg flex flex-col items-center">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#8b7355]"></div>
      <p className="mt-3 text-gray-700 font-medium">Loading data...</p>
    </div>
  </div>
)}
     
      <div className="bg-white mx-auto py-4 px-3 sm:py-6 sm:px-4 w-full">
        {/* Header */}
        <div className="flex flex-col space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="p-1.5 sm:p-2 bg-blue-600 rounded-lg">
                <Calculator className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                  Rental Calculator
                </h1>
              </div>
            </div>
            
            {/* Mobile Actions Menu */}
            <div className="lg:hidden">
              <Collapsible open={isActionsOpen} onOpenChange={setIsActionsOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="p-2">
                    <Menu className="h-4 w-4" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="absolute right-3 top-16 z-50 bg-white rounded-lg shadow-lg border p-2 space-y-2 min-w-48">
                  {hasCalculated && (
                    <Button 
                      onClick={() => {
                        setIsScheduleSidebarOpen(true);
                        setIsActionsOpen(false);
                      }}
                      className="w-full flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-sm text-white"
                      size="sm"
                    >
                      <Eye className="h-4 w-4" />
                      Visualise Schedule
                    </Button>
                  )}
                  {hasCalculated && (
                    <Button 
                      onClick={() => {
                        setIsIRRDialogOpen(true);
                        setIsActionsOpen(false);
                      }}
                      className="w-full flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-sm text-white"
                      size="sm"
                    >
                      <BarChart3 className="h-4 w-4" />
                      IRR Analysis
                    </Button>
                  )}

                  {customerId && (
                    <Button
                      onClick={() => {
                        handleSaveCalculation();
                        setIsActionsOpen(false);
                      }}
                      disabled={isSaving}
                      className="w-full flex items-center gap-2 bg-green-600 hover:bg-green-700 text-sm text-white"
                      size="sm"
                    >
                      <Save className="h-4 w-4" />
                      {isSaving ? "Saving..." : "Save Calculation"}
                    </Button>
                  )}

                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
          
          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-3 justify-end">
            {hasCalculated && (
              <>
                <Button 
                  onClick={() => setIsScheduleSidebarOpen(true)}
                  className="flex items-center gap-2 bg-[#cec8b0] hover:bg-[#e5d9bd]"
                >
                  <Eye className="h-4 w-4" />
                  Visualise
                </Button>
                <Button 
                  onClick={() => setIsIRRDialogOpen(true)}
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <BarChart3 className="h-4 w-4" />
                  IRR Analysis
                </Button>
                {customerId && (
                  <Button
                    onClick={() => {
                      handleSaveCalculation();
                      setIsActionsOpen(false);
                    }}
                    disabled={isSaving}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-sm text-white"
                    size="sm"
                  >
                    <Save className="h-4 w-4" />
                    {isSaving ? "Saving..." : "Save Calculation"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

         {/* Navigation Tabs */}
         <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4 sm:mb-6">
          <TabsList
            className={`grid w-full max-w-lg mx-auto lg:mx-0 lg:max-w-none lg:w-auto bg-white ${
              customerId ? "grid-cols-3" : "grid-cols-1"
            }`}
          >
            <TabsTrigger value="calculator" className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${activeTab === "calculator"
                          ? "bg-[#f0eadc]"
                          : "bg-white"
                          }`}> 
              <Calculator className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Calculator</span>
              <span className="sm:hidden">Calc</span>
            </TabsTrigger>
            {customerId && (
              <>
                <TabsTrigger value="saved" className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${activeTab === "saved"
                              ? "bg-[#f0eadc]"
                              : "bg-white"
                              }`}>
                  <History className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Saved ({savedCalculations.length})</span>
                  <span className="sm:hidden">Saved ({savedCalculations.length})</span>
                </TabsTrigger>
                <TabsTrigger value="termsheets" className={`flex items-center gap-1 sm:gap-2 text-xs sm:text-sm ${activeTab === "termsheets"
                              ? "bg-[#f0eadc]"
                              : "bg-white"
                              }`}>
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Term Sheets ({downloadedTermSheets.length})</span>
                  <span className="sm:hidden">Terms ({downloadedTermSheets.length})</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>

        <TabsContent value="calculator" className="space-y-4 sm:space-y-6">
        {/* Main Layout */}
        <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-6">
          {/* Input Form */}
          <div className="order-1">
            <Card className="shadow-lg border-0">
              <CardContent className="p-0">
                <InputForm 
                  inputs={inputs} 
                  onInputChange={handleInputChange} 
                  onCalculate={handleCalculate} 
                />
              </CardContent>
            </Card>
          </div>

          {/* Summary Section */}
          <div className="order-2">
            {isCalculating ? (
              <Card className="h-64 sm:h-96 shadow-lg border-0">
                <CardContent className="h-full flex items-center justify-center">
                  <div className="text-center px-4">
                    <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-blue-600 mx-auto"></div>
                    <p className="mt-4 sm:mt-6 text-gray-600 text-sm sm:text-lg">Calculating with Enhanced Goal Seek...</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">Factor-based interest calculations in progress</p>
                  </div>
                </CardContent>
              </Card>
            ) : hasCalculated ? (
              <>
                {/* Mobile collapsible */}
                <div className="lg:hidden">
                  <Collapsible open={isSummaryOpen} onOpenChange={setIsSummaryOpen}>
                    <Card className="shadow-lg border-0">
                      <CollapsibleTrigger asChild>
                        <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg cursor-pointer hover:from-green-700 hover:to-emerald-700 transition-colors">
                          <CardTitle className="text-lg sm:text-xl flex items-center justify-between">
                            Detailed Financial Summary
                            <span className="text-sm">{isSummaryOpen ? '▼' : '▶'}</span>
                          </CardTitle>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="p-0">
                          <SummarySection 
                            summary={summary} 
                            isCalculating={isCalculating} 
                          />
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                </div>
                {/* Desktop */}
                <div className="hidden lg:block">
                  <Card className="shadow-lg border-0">
                    <CardHeader className="bg-[#e7e4d9] text-black rounded-t-lg">
                      <CardTitle className="text-xl">Detailed Financial Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <SummarySection 
                        summary={summary} 
                        isCalculating={isCalculating} 
                      />
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <Card className="h-64 sm:h-96 shadow-lg border-0 border-dashed border-gray-300 lg:block hidden">
                <CardContent className="h-full flex items-center justify-center">
                  <div className="text-center p-4 sm:p-8">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calculator className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                    </div>
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Ready to Calculate</h3>
                    <p className="text-sm sm:text-base text-gray-500 max-w-md">Enter your rental parameters and dates. Disbursement amount will be calculated using Enhanced Goal Seek.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
        </TabsContent>

        {customerId && (
          <>
            <TabsContent value="saved">
              <SavedCalculations 
                calculations={savedCalculations}
                onLoad={handleLoadCalculation}
                onTermSheetDownload={handleTermSheetDownload}
                onUpdateCalculation={handleUpdateCalculation}
                onTermSheetDeleted={handleTermSheetDeleted}
                customerId={customerId}
              />
            </TabsContent>

            <TabsContent value="termsheets">
              <TermSheetHistory 
                termSheets={downloadedTermSheets}
                onUpdateTermSheet={handleUpdateTermSheet}
                customerId={customerId}
              />
            </TabsContent>
          </>
        )}

        </Tabs>

        {/* Payment Schedule Sidebar */}
        <PaymentScheduleSidebar 
          isOpen={isScheduleSidebarOpen}
          onClose={() => setIsScheduleSidebarOpen(false)}
          schedule={schedule}
          rentalCalculationBasis={inputs.rentalCalculationBasis}
        />

        {/* IRR Analysis Dialog */}
        <IRRCheckDialog
          open={isIRRDialogOpen}
          onOpenChange={setIsIRRDialogOpen}
          partialInputs={{
            totalAssetCost: inputs.totalRental,
            gstRate: inputs.gstRate * 100,
            tenureMonths: inputs.totalMonths,
            programLimit: inputs.totalRental,
            ptpmConsideration: inputs.ptpmConsideration,
            securityDepositPercent: inputs.securityDepositPercentage,
            purchasePricePercent: inputs.securityDepositPercentage - inputs.refundableSecurityPercentage,
            refundSecurityDepositPercent: inputs.refundableSecurityPercentage,
            processingFeesPercent: inputs.processingFeePercentage,
            gstPassback: (() => {
              if (!inputs.gstPassbackEnabled) return 0;
              const baseRental = inputs.totalRental / (1 + inputs.gstRate);
              const gstAmount = baseRental * inputs.gstRate;
              const periods = inputs.rentalFrequency === 'quarterly' ? inputs.totalMonths / 3 : inputs.totalMonths;
              return Math.round((gstAmount / periods + Number.EPSILON) * 100) / 100;
            })(),
            gstPassbackEnabled: inputs.gstPassbackEnabled,
            commencementDate: inputs.disbursementDate || '',
            paymentFrequency: inputs.rentalFrequency,
            advanceRentalPeriods: (() => {
              if (!inputs.advanceRentalEnabled) return 0;
              if (inputs.advanceRentalType === 'percentage') {
                // Convert percentage of invoice to equivalent periods
                const baseRental = inputs.totalRental / (1 + inputs.gstRate);
                const gstAmount = baseRental * inputs.gstRate;
                let gstPassback = 0;
                if (inputs.gstPassbackEnabled) {
                  const periods = inputs.rentalFrequency === 'quarterly' ? inputs.totalMonths / 3 : inputs.totalMonths;
                  gstPassback = Math.round((gstAmount / periods + Number.EPSILON) * 100) / 100;
                }
                const ptpmWithPassback = inputs.ptpmConsideration - gstPassback;
                const rentalPerPeriod = inputs.totalRental * ptpmWithPassback / 1000;
                const advanceAmount = inputs.totalRental * (inputs.advanceRentalCount / 100);
                return rentalPerPeriod > 0 ? advanceAmount / rentalPerPeriod : 0;
              }
              return inputs.advanceRentalCount;
            })(),
          }}
        />
      </div>
    </div>
  );
};

export default RentalCalculatorLayout;